import { motion } from 'framer-motion';
import { FileText, ChevronRight } from 'lucide-react';
import type { Article } from '@bcc/shared-types';
import { cn } from '../utils/cn';

export interface ArticleCardProps {
  article: Article;
  onClick?: () => void;
  isSelected?: boolean;
  showPreview?: boolean;
  className?: string;
}

export function ArticleCard({
  article,
  onClick,
  isSelected,
  showPreview = true,
  className,
}: ArticleCardProps) {
  const previewText = article.full_text.slice(0, 150);

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      className={cn(
        'p-4 rounded-xl border cursor-pointer transition-colors',
        isSelected
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-200 bg-white hover:border-gray-300',
        className
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'flex-shrink-0 h-10 w-10 rounded-lg flex items-center justify-center',
            isSelected ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
          )}
        >
          <FileText className="h-5 w-5" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono text-blue-600">
              {article.article_number}
            </span>
            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
              {article.code_name}
            </span>
          </div>

          <h4 className="mt-1 font-medium text-gray-900 truncate">
            {article.title}
          </h4>

          {showPreview && (
            <p className="mt-1 text-sm text-gray-500 line-clamp-2">
              {previewText}
              {article.full_text.length > 150 && '...'}
            </p>
          )}
        </div>

        <ChevronRight className="flex-shrink-0 h-5 w-5 text-gray-400" />
      </div>
    </motion.div>
  );
}
